import { ApiEndpoints } from "@shared/api/api-endpoints";
import { axApiClient } from "@shared/api/clients/apiClientFactory";

export const axScheduleApi = {
  getDashboardData: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "schedules/dashboard",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  getAllSchedules: async (
    remoteUtilityId: number,
    page: number,
    limit: number,
    search?: string,
    status?: number,
    frequencyType?: string,
    criticality?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "schedules",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) query.push("search", search);
        if (status) query.push("status", status);
        if (frequencyType) query.push("frequency_type", frequencyType);
        if (criticality) query.push("criticality", criticality);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getChoices: async (type: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `choices/${type}`
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  getAllSOPs: async (
    remoteUtilityId: string,
    readingSource: string,
    searchData?: string,
    sopAssociationType?: string,
    operationType?: string,
    page?: number,
    limit?: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order-template",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("reading_source", readingSource);
        query.push("page", page);
        query.push("limit", limit);
        if (searchData) query.push("search_data", searchData);
        if (sopAssociationType)
          query.push("association_type", sopAssociationType);
        if (operationType) query.push("operation_type", operationType);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getAllAssets: async (
    remoteUtilityId: number,
    page: number,
    limit: number,
    searchData?: string,
    type?: string,
    area?: string,
    system?: string,
    network?: string,
    facility?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);

        if (searchData) query.push("search", searchData);
        if (type) query.push("type", type);
        if (area) query.push("area", area);
        if (system) query.push("system", system);
        if (network) query.push("network", network);
        if (facility) query.push("facility", facility);
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getAllSystems: async (
    remoteUtilityId: number,
    isPaginationRequired: number,
    searchData?: string,
    type?: string,
    area?: string,
    facility?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "systems",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", isPaginationRequired);

        if (searchData) query.push("search", searchData);
        if (type) query.push("type", type);
        if (area) query.push("area", area);
        if (facility) query.push("facility", facility);
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getAllFacilities: async (
    remoteUtilityId: number,
    page: number,
    limit: number,
    searchData?: string,
    type?: string,
    area?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);

        if (searchData) query.push("search", searchData);
        if (type) query.push("facility_type", type);
        if (area) query.push("area", area);
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  getAllNetworks: async (
    remoteUtilityId: number,
    page: number,
    limit: number,
    isPaginationRequired: any,
    searchData?: string,
    type?: string,
    facility?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", page);
        query.push("limit", limit);
        query.push("is_pagination_required", isPaginationRequired);

        if (searchData) query.push("search", searchData);
        if (type) query.push("type", type);
        if (facility) query.push("facility_id", facility);
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },

  createSchedule: async (payload: any): Promise<any> => {
    try {
      const response = await axApiClient.post("schedules/", payload);
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSchedule: async (payload: any): Promise<any> => {
    try {
      const response = await axApiClient.patch(
        `schedules/${payload.id}/`,
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },

  getScheduleById: async (id: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `schedules/${id}`
    );
    const response = await axApiClient.get(url);
    return response.data.result ?? [];
  },
  updateScheduleStatus: async (data: any): Promise<any> => {
    const payload = {
      isActive: data.isActive ? 1 : 0,
    };

    try {
      const response = await axApiClient.patch(
        `schedules/${data.id}/status/`,
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  updateSOManually: async (data: any): Promise<any> => {
    const payload = {
      executionDate: data.executionDate,
      triggerType: data.triggerType,
    };

    try {
      const response = await axApiClient.post(
        `schedules/${data.id}/execute`,
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },
  getAllScheduleAssets: async (
    id: string,
    page: number,
    limit: number,
    remoteUtilityId: number,
    search?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `schedules/${id}/assets`,
      (query) => {
        query.push("page", page);
        query.push("limit", limit);
        query.push("remote_utility_id", remoteUtilityId);
        if (search) query.push("search", search);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getAllScheduleServiceOrderHistory: async (
    id: string,
    page: number,
    limit: number,
    remoteUtilityId: number,
    search?: string,
    status?: number,
    dateFrom?: string,
    dateTo?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `schedules/${id}/history`,
      (query) => {
        query.push("page", page);
        query.push("limit", limit);
        query.push("remote_utility_id", remoteUtilityId);
        if (search) query.push("search", search);
        if (status) query.push("status", status);
        if (dateFrom) query.push("date_from", dateFrom);
        if (dateTo) query.push("date_to", dateTo);
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data ?? [];
  },
  getSOPsForEventRules: async (
    remoteUtilityId: string,
    associationType?: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order-template",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("reading_source", "AX");
        if (associationType) {
          query.push("association_type", associationType);
        }
        return query;
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
};
